import React, { useEffect, useRef, useState } from 'react';
import {
  Sparkles, Download, Copy, Trash2, RefreshCw, Loader2, Video, Play, ChevronDown,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { dealerReels, dealerListings, buildImageUrl } from '../../services/dealerApi';
import { toast } from '../../hooks/use-toast';
import CreateReelModal from '../../components/dealers/CreateReelModal';

const STATUS_COLORS = {
  queued: 'bg-slate-500/20 text-slate-300 border-slate-500/30',
  rendering: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
  ready: 'bg-green-500/20 text-green-300 border-green-500/30',
  failed: 'bg-red-500/20 text-red-300 border-red-500/30',
};

const Reels = () => {
  const [reels, setReels] = useState([]);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openReelId, setOpenReelId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [listings, setListings] = useState([]);
  const [pickedListing, setPickedListing] = useState(null);
  const [regen, setRegen] = useState({});
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const [r, u] = await Promise.all([dealerReels.list(), dealerReels.usage()]);
      setReels(r.reels || []);
      setUsage(u);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Poll while anything is queued/rendering. Tolerate transient 5xx
    // (e.g. preview-env 502s while FFmpeg is at high CPU).
    pollRef.current = setInterval(async () => {
      try {
        const res = await dealerReels.list();
        const anyPending = res.reels?.some(
          (x) => x.status === 'queued' || x.status === 'rendering',
        );
        if (anyPending) load();
      } catch {
        // ignore transient errors; next tick will retry
      }
    }, 6000);
    return () => clearInterval(pollRef.current);
  }, []);

  const openPicker = async () => {
    setPickerOpen(true);
    try {
      const res = await dealerListings.list();
      setListings((res.listings || []).filter((l) => (l.photos || []).length > 0));
    } catch {
      setListings([]);
    }
  };

  const handleListingChosen = (l) => {
    setPickedListing(l);
    setPickerOpen(false);
    setCreateOpen(true);
  };

  const handleCopy = async (text, what) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: `${what} copied!` });
    } catch {
      toast({ title: 'Copy failed', variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this reel?')) return;
    await dealerReels.remove(id);
    toast({ title: 'Reel deleted' });
    load();
  };

  const handleRetry = async (id) => {
    try {
      await dealerReels.retry(id);
      toast({ title: 'Re-queued for render' });
      load();
    } catch (err) {
      toast({
        title: 'Retry failed',
        description: err?.response?.data?.detail || 'Try again in a moment.',
        variant: 'destructive',
      });
    }
  };

  const handleRegen = async (id, tone = null) => {
    setRegen((p) => ({ ...p, [id]: true }));
    try {
      const res = await dealerReels.regenerateCaption(id, tone);
      setReels((prev) =>
        prev.map((r) => (r.id === id ? { ...r, caption: res.caption, hashtags: res.hashtags } : r)),
      );
      toast({ title: 'Caption refreshed' });
    } catch (e) {
      toast({ title: 'Failed to regenerate', variant: 'destructive' });
    } finally {
      setRegen((p) => ({ ...p, [id]: false }));
    }
  };

  return (
    <div data-testid="reels-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <span className="text-xs uppercase tracking-[0.2em] text-cyan-400 font-medium flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" /> AI Reels
          </span>
          <h1 className="mt-1 text-3xl md:text-4xl font-extrabold text-white tracking-tight">
            Instagram Reels Studio
          </h1>
          <p className="text-sm text-slate-400 mt-1 max-w-xl">
            Generate ready-to-post 9:16 vertical reels from your inventory — auto-branded, AI-captioned.
          </p>
        </div>
        <Button
          onClick={openPicker}
          data-testid="reels-new-btn"
          className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold rounded-xl"
        >
          <Sparkles className="w-4 h-4 mr-2" /> New Reel
        </Button>
      </div>

      {/* Quota */}
      {usage && (
        <div className="bg-slate-900/40 backdrop-blur-xl border border-slate-700/50 rounded-2xl px-5 py-4 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400">Monthly Quota</div>
            <div className="text-xl font-bold text-white mt-0.5">
              {usage.used} <span className="text-slate-500">/</span> {usage.limit}{' '}
              <span className="text-sm text-slate-400 font-normal">reels used</span>
            </div>
          </div>
          <div className="flex-1 min-w-[150px] max-w-xs">
            <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-cyan-400 to-blue-500"
                style={{ width: `${Math.min(100, (usage.used / Math.max(1, usage.limit)) * 100)}%` }}
              />
            </div>
            <div className="text-[11px] text-slate-500 mt-1">
              {usage.remaining} reels remaining this month
            </div>
          </div>
        </div>
      )}

      {/* Reels list */}
      {loading ? (
        <div className="text-center py-12 text-slate-400">Loading...</div>
      ) : reels.length === 0 ? (
        <div className="bg-slate-900/40 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-12 text-center">
          <Video className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400">No reels yet. Generate one from an inventory listing.</p>
          <Button
            onClick={openPicker}
            className="mt-4 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold rounded-xl"
          >
            <Sparkles className="w-4 h-4 mr-2" /> Create your first reel
          </Button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {reels.map((r) => {
            const isOpen = openReelId === r.id;
            const isWorking = r.status === 'queued' || r.status === 'rendering';
            return (
              <div
                key={r.id}
                data-testid={`reel-card-${r.id}`}
                className="bg-slate-900/40 backdrop-blur-xl border border-slate-700/50 rounded-2xl overflow-hidden"
              >
                {/* Preview */}
                <div className="aspect-[9/16] bg-slate-950 relative">
                  {r.status === 'ready' && r.video_url ? (
                    <video
                      src={buildImageUrl(r.video_url)}
                      poster={r.thumbnail_url ? buildImageUrl(r.thumbnail_url) : undefined}
                      controls
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                      {isWorking ? (
                        <>
                          <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
                          <span className="text-xs text-cyan-300 uppercase tracking-wider">
                            {r.status === 'queued' ? 'In queue' : 'Rendering...'}
                          </span>
                        </>
                      ) : r.status === 'failed' ? (
                        <>
                          <Video className="w-8 h-8 text-red-400" />
                          <span className="text-xs text-red-300 uppercase tracking-wider text-center px-3">
                            {r.error || 'Render failed'}
                          </span>
                          <Button
                            size="sm"
                            onClick={() => handleRetry(r.id)}
                            data-testid={`reel-retry-${r.id}`}
                            className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold mt-2"
                          >
                            Retry
                          </Button>
                        </>
                      ) : (
                        <Play className="w-10 h-10 text-slate-700" />
                      )}
                    </div>
                  )}
                  <span
                    className={`absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${STATUS_COLORS[r.status] || ''}`}
                  >
                    {r.status}
                  </span>
                </div>

                {/* Body */}
                <div className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="text-[11px] uppercase tracking-wider text-cyan-400 font-semibold">
                        {r.template.replace(/_/g, ' ')}
                      </div>
                      <div className="text-xs text-slate-400 mt-0.5">
                        {r.duration_seconds ? `${r.duration_seconds}s · ` : ''}{r.music_track?.replace('.mp3', '')}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(r.id)}
                      className="border-red-500/30 bg-red-500/10 text-red-300 hover:bg-red-500/20 h-8 px-2"
                      data-testid={`reel-delete-${r.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>

                  {r.status === 'ready' && (
                    <>
                      <div className="flex gap-2">
                        <a
                          href={buildImageUrl(r.video_url)}
                          download={`reel-${r.id}.mp4`}
                          className="flex-1"
                        >
                          <Button
                            size="sm"
                            className="w-full bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold"
                            data-testid={`reel-download-${r.id}`}
                          >
                            <Download className="w-3 h-3 mr-1" /> Download MP4
                          </Button>
                        </a>
                      </div>

                      <button
                        onClick={() => setOpenReelId(isOpen ? null : r.id)}
                        className="w-full text-left text-xs text-slate-400 hover:text-cyan-300 flex items-center gap-1 pt-1"
                      >
                        <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                        {isOpen ? 'Hide' : 'Show'} caption & hashtags
                      </button>

                      {isOpen && (
                        <div className="space-y-2 pt-1">
                          <div className="bg-slate-950/60 rounded-lg p-3 border border-slate-700/50">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-[10px] uppercase tracking-wider text-slate-500">Caption</span>
                              <button
                                onClick={() => handleCopy(r.caption || '', 'Caption')}
                                className="text-[10px] text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
                                data-testid={`reel-copy-caption-${r.id}`}
                              >
                                <Copy className="w-3 h-3" /> Copy
                              </button>
                            </div>
                            <pre className="text-xs text-slate-200 whitespace-pre-wrap font-sans">
                              {r.caption}
                            </pre>
                          </div>

                          <div className="bg-slate-950/60 rounded-lg p-3 border border-slate-700/50">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-[10px] uppercase tracking-wider text-slate-500">Hashtags</span>
                              <button
                                onClick={() => handleCopy((r.hashtags || []).join(' '), 'Hashtags')}
                                className="text-[10px] text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
                                data-testid={`reel-copy-hashtags-${r.id}`}
                              >
                                <Copy className="w-3 h-3" /> Copy
                              </button>
                            </div>
                            <div className="text-xs text-cyan-300 leading-relaxed">
                              {(r.hashtags || []).join(' ')}
                            </div>
                          </div>

                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRegen(r.id)}
                            disabled={!!regen[r.id]}
                            className="w-full border-slate-700 bg-slate-900/40 text-slate-200"
                            data-testid={`reel-regen-${r.id}`}
                          >
                            {regen[r.id] ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                            Regenerate caption
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Listing picker dialog (when "New Reel" clicked without a listing) */}
      {pickerOpen && (
        <div
          className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-4"
          onClick={() => setPickerOpen(false)}
        >
          <div
            className="bg-slate-900 border border-slate-700 rounded-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto p-5 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-white">Pick a listing</h3>
            {listings.length === 0 ? (
              <p className="text-slate-400 text-sm">
                No listings with photos found. Add a car first.
              </p>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3">
                {listings.map((l) => {
                  const cover = l.photos?.find((p) => p.enhanced_url)?.enhanced_url || l.photos?.[0]?.url;
                  return (
                    <button
                      key={l.id}
                      onClick={() => handleListingChosen(l)}
                      data-testid={`reel-picker-${l.id}`}
                      className="flex items-center gap-3 p-3 bg-slate-950/40 border border-slate-700/50 rounded-xl hover:border-cyan-500/40 transition-colors text-left"
                    >
                      <div className="w-14 h-14 rounded-lg bg-slate-800 overflow-hidden flex-shrink-0">
                        {cover && <img src={buildImageUrl(cover)} alt="" className="w-full h-full object-cover" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-white truncate text-sm">
                          {l.year} {l.ai_details?.make} {l.ai_details?.model}
                        </div>
                        <div className="text-xs text-slate-400">
                          ₹{(l.price || 0).toLocaleString('en-IN')} · {l.photos?.length} photos
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
            <div className="flex justify-end">
              <Button
                variant="outline"
                onClick={() => setPickerOpen(false)}
                className="border-slate-700 bg-slate-900/40 text-slate-200"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      <CreateReelModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        listing={pickedListing}
        onCreated={() => load()}
      />
    </div>
  );
};

export default Reels;
